import { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";
import { trackShowroomBookingStarted, trackShowroomBookingCompleted } from "@/lib/analytics";
import collMushroom from "@/assets/farve/coll-mushroom.jpg";

export const visitHeadDa = () => ({
  meta: [
    { title: "Book et besøg — Farveladeland" },
    {
      name: "description",
      content:
        "Book et privat besøg i Farveladelands showroom i København, eller send os et spørgsmål om et bestemt stykke.",
    },
    { property: "og:title", content: "Book et besøg — Farveladeland" },
    {
      property: "og:description",
      content: "Private viewings af vintage Murano-glas i København.",
    },
    { property: "og:image", content: collMushroom },
  ],
});

export const visitHeadEn = () => ({
  meta: [
    { title: "Book a visit — Farveladeland" },
    {
      name: "description",
      content:
        "Book a private viewing at Farveladeland's Copenhagen showroom, or send us a question about a specific piece.",
    },
    { property: "og:title", content: "Book a visit — Farveladeland" },
    {
      property: "og:description",
      content: "Private viewings of vintage Murano glass in Copenhagen.",
    },
    { property: "og:image", content: collMushroom },
  ],
});

export function VisitPage() {
  const { t } = useT();
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    trackShowroomBookingStarted("visit_page");
  }, []);

  const info = [
    [t("visit.info.hours.k"), t("visit.info.hours.v")],
    [t("visit.info.email.k"), t("visit.info.email.v")],
    [t("visit.info.ig.k"), t("visit.info.ig.v")],
    [t("visit.info.dispatch.k"), t("visit.info.dispatch.v")],
  ] as const;

  return (
    <section className="pt-40 pb-32 md:pt-52">
      <div className="mx-auto grid max-w-[1500px] gap-16 px-6 md:grid-cols-12 md:px-10">
        <div className="md:col-span-5">
          <p className="eyebrow">{t("visit.eyebrow")}</p>
          <h1 className="mt-4 font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95]">
            {t("visit.titleA")}
            <br />
            <em className="text-terracotta">{t("visit.titleB")}</em>
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground">
            {t("visit.body")}
          </p>

          <div className="mt-12 space-y-6 border-t border-border pt-10 text-sm text-foreground">
            {info.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-6">
                <span className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{k}</span>
                <span className="text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="rounded-sm border border-border bg-card p-8 md:p-12">
            {submitted ? (
              <div className="py-12 text-center">
                <p className="eyebrow">{t("visit.thanks.eyebrow")}</p>
                <h2 className="mt-4 font-display text-4xl">{t("visit.thanks.title")}</h2>
                <p className="mt-4 text-muted-foreground">{t("visit.thanks.body")}</p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  trackShowroomBookingCompleted();
                  setSubmitted(true);
                }}
                className="space-y-6"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label={t("visit.form.name")} name="name" required />
                  <Field label={t("visit.form.email")} name="email" type="email" required />
                </div>
                <Field label={t("visit.form.date")} name="date" type="date" />
                <div>
                  <label htmlFor="visit-message" className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    {t("visit.form.message")}
                  </label>
                  <textarea
                    id="visit-message"
                    name="message"
                    rows={5}
                    placeholder={t("visit.form.placeholder")}
                    className="mt-2 w-full resize-none border-b border-border bg-transparent py-2 text-base text-foreground placeholder:text-muted-foreground/60 focus:border-terracotta focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="mt-4 inline-flex items-center gap-3 rounded-full bg-foreground px-6 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-background transition-colors hover:bg-terracotta"
                >
                  {t("visit.form.submit")} →
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}) {
  const id = `visit-${name}`;
  return (
    <div>
      <label htmlFor={id} className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
        {label}
        {required && <span className="ml-1 text-terracotta">*</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        className="mt-2 w-full border-b border-border bg-transparent py-2 text-base text-foreground placeholder:text-muted-foreground/60 focus:border-terracotta focus:outline-none"
      />
    </div>
  );
}
